/**
 * Row-rollup dimensions for the Inventory grids — the single source of truth shared by
 * the grid itself (`containers/inventory.tsx`) and the brand-default picker in Settings.
 *
 * The grid appends the merchant's entity attributes to this list at runtime as
 * `attr:<entityAttributeId>`; Settings offers the static dimensions only. Both resolve a
 * stored value against the list they hold and fall back to NO_ROLLUP when it no longer
 * exists, so a deleted attribute degrades to "No grouping" rather than a broken grid.
 */

export const NO_ROLLUP = 'none';

/** Rollup keys for entity attributes are namespaced to avoid colliding with static ones. */
export const ATTR_ROLLUP_PREFIX = 'attr:';

export const UNASSIGNED_GROUP_LABEL = 'Unassigned';

export type RollupOption = { label: string; value: string };

export const STATIC_ROLLUP_OPTIONS: RollupOption[] = [
    { label: 'No grouping', value: NO_ROLLUP },
    { label: 'Category', value: 'category' },
    { label: 'Tier', value: 'tier' },
    { label: 'Product Group', value: 'productGroup' },
    { label: 'Active Status', value: 'isActive' },
];

/**
 * A brand default is only usable if the dimension it names still exists — entity
 * attributes are per-merchant and can be deleted after the default was saved.
 * Returns null for anything unresolvable so the caller falls through to NO_ROLLUP.
 */
export const resolveOrgRollupDefault = (
    stored: string | null | undefined,
    attrKeys: string[],
): string | null => {
    if (!stored) return null;
    const known =
        STATIC_ROLLUP_OPTIONS.some((o) => o.value === stored) ||
        attrKeys.includes(stored);
    return known ? stored : null;
};

/**
 * Effective grouping for a tab: the user's own choice wins, then the brand default,
 * then the flat view. `userChoice` is null when this user has never picked one —
 * see the usePersistentState mount-write note in `hooks/usePersistentState.test.ts`.
 */
export const resolveEffectiveRollup = (
    userChoice: string | null | undefined,
    orgDefault: string | null | undefined,
): string => userChoice ?? orgDefault ?? NO_ROLLUP;

/**
 * Whether grouping is actually applied right now.
 *
 * A search term suspends it: rollup rows are keyed by group name, so a SKU-level term
 * can never match one and would render an empty table even though the product sits in
 * a group's members. A term therefore drops the view back to flat rows so the server's
 * `sku ILIKE … OR name ILIKE …` search can answer it; clearing the box restores grouping.
 */
export const isRollupActive = (
    rollupBy: string,
    searchTerm: string | null | undefined,
): boolean => rollupBy !== NO_ROLLUP && !(searchTerm ?? '').trim();
